import React, { useRef, useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux';
import { IoChevronUpOutline, IoDocumentTextOutline, IoJournalOutline, IoCubeOutline, IoBarChartOutline } from 'react-icons/io5';

import { Nav } from 'react-bootstrap';

// Assets
import { ReactComponent as HomeIcon } from '../../assets/home-icon.svg';
import { ReactComponent as DocumentIcon } from '../../assets/document.svg';
import { ReactComponent as WarehouseIcon } from '../../assets/warehouse.svg';
import { ReactComponent as ContainerIcon } from '../../assets/container.svg';
import { ReactComponent as DocumentXml } from '../../assets/document-xml.svg';

import { setActiveTenant } from '../../store/reducers/users';
import { resetTenant } from '../../store/reducers/tenant';
import { fetchBranches } from '../../store/actions/branch';
import { resetBranch } from '../../store/reducers/branch';
import { resetEvidenceList } from '../../store/reducers/evidenceList';
import { warningGlobal } from '../../store/reducers/notifications';

const DISABLED_LINK_STYLE = { opacity: 0.35, cursor: 'not-allowed', pointerEvents: 'none' };

const Sidebar = ({ onClose }) => {

    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const userData = useSelector(state => state.users.data);
    const tenantList = userData?.tenantList ?? [];
    const activeTenantId = userData?.activeTenantId;
    const activeTenantBlocked = userData?.activeTenantBlocked;
    const activeTenant = tenantList.find(t => t._id === activeTenantId);
    const modules = userData?.modules;
    const hasModule = (key) => modules === 'all' || modules === undefined || (Array.isArray(modules) && modules.includes(key));
    const operationalLogBlocked = activeTenantBlocked || !hasModule('operationalLog');
    const ppwrNavBlocked = activeTenantBlocked;
    const wasteBlocked = activeTenantBlocked;

    const [tenantMenuOpen, setTenantMenuOpen] = useState(false);
    const tenantMenuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (tenantMenuRef.current && !tenantMenuRef.current.contains(e.target)) {
                setTenantMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const switchTenant = (tenantId) => {
        if (tenantId === activeTenantId) return;
        const targetTenant = tenantList.find(t => t._id === tenantId);
        setTenantMenuOpen(false);
        dispatch(setActiveTenant(tenantId));
        dispatch(resetBranch());
        dispatch(resetTenant());
        dispatch(resetEvidenceList());
        if (targetTenant?.blocked) {
            dispatch(warningGlobal('Tento subjekt je zablokovaný. Nemôžete vykonávať žiadnu činnosť.'));
        } else {
            dispatch(fetchBranches());
        }
        navigate('/dashboard');
        onClose?.();
    };

  return (
    <Nav className="sidebar-nav">
        <div className="sidebar-links">
            <Nav.Item>
                <Nav.Link
                    as={activeTenantBlocked ? 'span' : RouterLink}
                    to={activeTenantBlocked ? undefined : '/dashboard'}
                    className={`navLink ${location.pathname === '/dashboard' ? 'active' : ''}`}
                    style={activeTenantBlocked ? DISABLED_LINK_STYLE : undefined}
                    onClick={activeTenantBlocked ? undefined : onClose}
                >
                    <HomeIcon className="iconNav" />
                    Prehľady
                </Nav.Link>
            </Nav.Item>

            <span className="sidebar-section-label">Evidencia odpadov</span>
            <Nav.Item>
                <Nav.Link
                    as={wasteBlocked ? 'span' : RouterLink}
                    to={wasteBlocked ? undefined : '/statistics'}
                    className={`navLink ${location.pathname === '/statistics' ? 'active' : ''}`}
                    style={wasteBlocked ? DISABLED_LINK_STYLE : undefined}
                    onClick={wasteBlocked ? undefined : onClose}
                >
                    <IoBarChartOutline className="iconNav" />
                    Štatistiky
                </Nav.Link>
                <Nav.Link
                    as={wasteBlocked ? 'span' : RouterLink}
                    to={wasteBlocked ? undefined : '/evidencelist'}
                    className={`navLink ${location.pathname === '/evidencelist' ? 'active' : ''}`}
                    style={wasteBlocked ? DISABLED_LINK_STYLE : undefined}
                    onClick={wasteBlocked ? undefined : onClose}
                >
                    <DocumentIcon className="iconNav" />
                    Evidenčné listy
                </Nav.Link>
                <Nav.Link
                    as={wasteBlocked ? 'span' : RouterLink}
                    to={wasteBlocked ? undefined : '/announcement'}
                    className={`navLink ${location.pathname === '/announcement' ? 'active' : ''}`}
                    style={wasteBlocked ? DISABLED_LINK_STYLE : undefined}
                    onClick={wasteBlocked ? undefined : onClose}
                >
                    <DocumentXml className="iconNav" />
                    Ohlásenie odpadov
                </Nav.Link>
                <Nav.Link
                    as={wasteBlocked ? 'span' : RouterLink}
                    to={wasteBlocked ? undefined : '/consents'}
                    className={`navLink ${location.pathname === '/consents' ? 'active' : ''}`}
                    style={wasteBlocked ? DISABLED_LINK_STYLE : undefined}
                    onClick={wasteBlocked ? undefined : onClose}
                >
                    <IoDocumentTextOutline className="iconNav" />
                    Súhlasy
                </Nav.Link>
                <Nav.Link
                    as={operationalLogBlocked ? 'span' : RouterLink}
                    to={operationalLogBlocked ? undefined : '/operational-log'}
                    className={`navLink ${location.pathname === '/operational-log' ? 'active' : ''}`}
                    style={operationalLogBlocked ? DISABLED_LINK_STYLE : undefined}
                    onClick={operationalLogBlocked ? undefined : onClose}
                >
                    <IoJournalOutline className="iconNav" />
                    Prevádzkový denník
                </Nav.Link>
            </Nav.Item>

            <span className="sidebar-section-label">Obalové hospodárstvo</span>
            <Nav.Item>
                <Nav.Link
                    as={ppwrNavBlocked ? 'span' : RouterLink}
                    to={ppwrNavBlocked ? undefined : '/ppwr'}
                    className={`navLink ${location.pathname === '/ppwr' ? 'active' : ''}`}
                    style={ppwrNavBlocked ? DISABLED_LINK_STYLE : undefined}
                    onClick={ppwrNavBlocked ? undefined : onClose}
                >
                    <IoCubeOutline className="iconNav" />
                    PPWR
                </Nav.Link>
            </Nav.Item>

            <span className="sidebar-section-label">Prevádzka</span>
            <Nav.Item>
                <Nav.Link
                    as={wasteBlocked ? 'span' : RouterLink}
                    to={wasteBlocked ? undefined : '/warehouse'}
                    className={`navLink ${location.pathname === '/warehouse' ? 'active' : ''}`}
                    style={wasteBlocked ? DISABLED_LINK_STYLE : undefined}
                    onClick={wasteBlocked ? undefined : onClose}
                >
                    <WarehouseIcon className="iconNav" />
                    Sklady
                </Nav.Link>
                <Nav.Link
                    as={wasteBlocked ? 'span' : RouterLink}
                    to={wasteBlocked ? undefined : '/container'}
                    className={`navLink ${location.pathname === '/container' ? 'active' : ''}`}
                    style={wasteBlocked ? DISABLED_LINK_STYLE : undefined}
                    onClick={wasteBlocked ? undefined : onClose}
                >
                    <ContainerIcon className="iconNav" />
                    Nádoby
                </Nav.Link>
            </Nav.Item>
        </div>

        <div className="sidebar-footer">
            {activeTenant && (
                <div className="notification-center" ref={tenantMenuRef}>
                    {tenantList.length > 1 ? (
                        <>
                            <button
                                className="sidebar-tenant-card sidebar-tenant-card--clickable"
                                onClick={() => setTenantMenuOpen(o => !o)}
                            >
                                <div className="sidebar-tenant-info">
                                    <div className="sidebar-tenant-name">{activeTenant.companyName}</div>
                                    <div className="sidebar-user-name">{userData?.name} {userData?.surname}</div>
                                </div>
                                <IoChevronUpOutline size={14} className={`sidebar-tenant-chevron${tenantMenuOpen ? ' open' : ''}`} style={{ flexShrink: 0, color: '#a0aec0' }} />
                            </button>
                            {tenantMenuOpen && (
                                <div className="notification-dropdown tenant-menu-dropdown">
                                    <div className="notification-header">Organizácie</div>
                                    {tenantList.map(t => (
                                        <button
                                            key={t._id}
                                            className={`user-menu-item select-branch-item${t._id === activeTenantId ? ' active' : ''}`}
                                            onClick={() => switchTenant(t._id)}
                                        >
                                            {t.companyName}
                                            {t.blocked && (
                                                <span style={{ fontSize: 11, color: '#e53e3e', marginLeft: 6 }}>zablokovaný</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="sidebar-tenant-card">
                            <div className="sidebar-tenant-info">
                                <div className="sidebar-tenant-name">{activeTenant.companyName}</div>
                                <div className="sidebar-user-name">{userData?.name} {userData?.surname}</div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    </Nav>
  );
};

export default Sidebar;
